import { useMemo } from "react";
import { useApiClient } from "../apiClients";
import { ENDPOINTS_PAIEMENT } from "./endpoints";

export { lienParrainage, lienWhatsApp } from "./apporteursLiens";

// Canal apporteurs : des animateurs promeuvent Tilmicode dans leurs écoles.
// Chacun a UN code actif ; la plateforme compte, chaque mois, les enfants
// qu'il a apportés. Aucune commission n'est calculée ici.

export type TTypeAvantage = "aucun" | "pourcentage" | "montant_fixe" | "prix_fixe";

export type TPrixFixe = {
  offre: string;
  devise: string;
  prix: number;
};

/** Ce que le code change au prix, à chaque paiement (renouvellements compris). */
export type TAvantage = {
  type: TTypeAvantage;
  /** Pourcentage (0 < v < 100) ou montant retiré au prix mensuel. */
  valeur?: number;
  /** Devise du montant fixe. */
  devise?: string;
  /** Prix mensuels imposés, par offre et par devise (« prix_fixe »). */
  prix?: TPrixFixe[];
};

export type TCodeApporteur = {
  id: number;
  code: string;
  actif: boolean;
  avantage: TAvantage;
};

export type TApporteur = {
  id: number;
  userapp_id: number;
  actif: boolean;
  etat_le: string;
  cree_le: string;
  prenom?: string;
  nom?: string;
  email?: string;
  login?: string;
  /** Le code actif, ou à défaut le dernier. */
  code?: TCodeApporteur;
  /** Fourni par le serveur quand APPORTEUR_LIEN_INSCRIPTION est réglé. */
  lien_parrainage?: string;
};

export type TCreationApporteur = {
  prenom: string;
  nom: string;
  email: string;
  code: string;
  avantage: TAvantage;
  lang?: string;
};

export type TApporteurCree = {
  apporteur: TApporteur;
  /** Rendu UNE SEULE FOIS : il n'est stocké que haché par tc_identity. */
  mot_de_passe_provisoire: string;
  lien_parrainage?: string;
};

/** Ce qu'un visiteur apprend d'un code : jamais l'identité de l'animateur. */
export type TVerificationCode = {
  statut: 'valide' | 'inactif' | 'inconnu' | 'invalide';
  avantage?: TAvantage;
};

export type TStatutEnfant = 'premium' | 'freemium';

export type TEnfantAttribue = {
  enfant_id: number;
  prenom?: string;
  nom?: string;
  /** Compte supprimé : ses mois passés restent comptés. */
  supprime: boolean;
  statut: TStatutEnfant;
  source: 'inscription_parent' | 'paiement' | 'ecole' | 'correction';
  depuis: string;
  parents: { id: number; prenom?: string; nom?: string }[];
  ecoles: { id: number; nom: string }[];
};

export type TDetailMois = {
  mois: string;
  instant_reference: string;
  en_cours: boolean;
  premium: number;
  freemium: number;
  enfants: TEnfantAttribue[];
};

export type TCelluleTableau = {
  mois: string;
  premium: number;
  freemium: number;
  /** Une correction enregistrée après la fin du mois l'a modifié. */
  modifie_apres_coup: boolean;
};

export type TLigneTableau = {
  apporteur_id: number;
  prenom?: string;
  nom?: string;
  code?: string;
  actif: boolean;
  cellules: TCelluleTableau[];
};

/** École associée à un animateur : ses élèves rattachés ensuite lui sont attribués. */
export type TAssociationEcole = {
  id: number;
  apporteur_id: number;
  organisation_id: number;
  nom: string;
  debut: string;
  /** Absente : association en cours. */
  fin?: string;
  eleves_attribues: number;
};

export type TOrganisation = {
  id: number;
  nom: string;
  /** Son animateur actuel, si elle est associée. */
  apporteur_id?: number;
};

export type TLigneHistorique = {
  id: number;
  apporteur_id: number;
  source: TEnfantAttribue['source'];
  debut: string;
  fin?: string;
  motif_fin?: string;
  annulee: boolean;
};

export type TCorrection = {
  id: number;
  enfant_id: number;
  ancien_apporteur_id?: number;
  nouvel_apporteur_id?: number;
  effet_le: string;
  motif: string;
  par: number;
  cree_le: string;
};

export type THistoriqueEnfant = {
  enfant_id: number;
  prenom?: string;
  nom?: string;
  attributions: TLigneHistorique[];
  corrections: TCorrection[];
};

export type TDemandeCorrection = {
  enfant_id: number;
  /** null : l'enfant n'est plus attribué à personne. */
  apporteur_id: number | null;
  effet_le: string;
  motif: string;
};

export const useApporteursApi = () => {
  const client = useApiClient("paiement");

  return useMemo(
    () => ({
      moi: async (): Promise<TApporteur> => {
        const { data } = await client.get<TApporteur>(ENDPOINTS_PAIEMENT.APPORTEURS.MOI);
        return data;
      },
      lister: async (): Promise<TApporteur[]> => {
        const { data } = await client.get<{ items: TApporteur[] }>(ENDPOINTS_PAIEMENT.APPORTEURS.ADMIN);
        return data?.items ?? [];
      },
      creer: async (payload: TCreationApporteur): Promise<TApporteurCree> => {
        const { data } = await client.post<TApporteurCree>(ENDPOINTS_PAIEMENT.APPORTEURS.ADMIN, payload);
        return data;
      },
      definirCode: async (id: number, code: string, avantage: TAvantage): Promise<TApporteur> => {
        const { data } = await client.put<TApporteur>(ENDPOINTS_PAIEMENT.APPORTEURS.ADMIN_PARRAINAGE(id), { code, avantage });
        return data;
      },
      definirActif: async (id: number, actif: boolean): Promise<TApporteur> => {
        const { data } = await client.patch<TApporteur>(ENDPOINTS_PAIEMENT.APPORTEURS.ADMIN_UN(id), { actif });
        return data;
      },
      /** Route publique : utilisable avant d'avoir un compte. */
      verifierCode: async (code: string): Promise<TVerificationCode> => {
        const { data } = await client.get<TVerificationCode>(ENDPOINTS_PAIEMENT.APPORTEURS.VERIFIER_CODE(code));
        return data;
      },
      /** Mois de l'animateur CONNECTÉ (AAAA-MM, le mois en cours par défaut). */
      monMois: async (mois?: string): Promise<TDetailMois> => {
        const { data } = await client.get<TDetailMois>(ENDPOINTS_PAIEMENT.APPORTEURS.MON_MOIS(mois));
        return data;
      },
      tableau: async (de?: string, a?: string): Promise<{ mois: string[]; lignes: TLigneTableau[] }> => {
        const { data } = await client.get<{ mois: string[]; lignes: TLigneTableau[] }>(
          ENDPOINTS_PAIEMENT.APPORTEURS.TABLEAU(de, a)
        );
        return { mois: data?.mois ?? [], lignes: data?.lignes ?? [] };
      },
      moisAdmin: async (id: number, mois: string): Promise<TDetailMois> => {
        const { data } = await client.get<TDetailMois>(ENDPOINTS_PAIEMENT.APPORTEURS.ADMIN_MOIS(id, mois));
        return data;
      },
      ecoles: async (): Promise<TAssociationEcole[]> => {
        const { data } = await client.get<{ ecoles: TAssociationEcole[] }>(ENDPOINTS_PAIEMENT.APPORTEURS.ECOLES);
        return data?.ecoles ?? [];
      },
      organisations: async (q: string): Promise<TOrganisation[]> => {
        const { data } = await client.get<{ organisations: TOrganisation[] }>(ENDPOINTS_PAIEMENT.APPORTEURS.ORGANISATIONS(q));
        return data?.organisations ?? [];
      },
      associerEcole: async (apporteurId: number, organisationId: number): Promise<TAssociationEcole[]> => {
        const { data } = await client.post<{ ecoles: TAssociationEcole[] }>(
          ENDPOINTS_PAIEMENT.APPORTEURS.ASSOCIER_ECOLE(apporteurId), { organisation_id: organisationId }
        );
        return data?.ecoles ?? [];
      },
      dissocierEcole: async (associationId: number): Promise<void> => {
        await client.post(ENDPOINTS_PAIEMENT.APPORTEURS.DISSOCIER_ECOLE(associationId));
      },
      historiqueEnfant: async (id: number): Promise<THistoriqueEnfant> => {
        const { data } = await client.get<THistoriqueEnfant>(ENDPOINTS_PAIEMENT.APPORTEURS.ENFANT(id));
        return data;
      },
      corriger: async (demande: TDemandeCorrection): Promise<THistoriqueEnfant> => {
        const { data } = await client.post<THistoriqueEnfant>(ENDPOINTS_PAIEMENT.APPORTEURS.CORRECTIONS, demande);
        return data;
      },
    }),
    [client]
  );
};
